import React from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  SxProps,
  Theme,
} from "@mui/material";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import { toPersianNumber } from "@/hooks/useThousandsSeparator";
import TypoGraphyKit from "../../kit/typoGraphy";

interface CustomSelectProps<T extends string | number> {
  label: React.ReactNode;
  options: T[];
  value: T;
  onChange: (value: T) => void;
  sx?: SxProps<Theme>;
}
/**
 * CustomSelect
 *
 * نسخه‌ی اصلاح‌شده‌ی کامپوننت سلکت با پشتیبانی کامل از پراپرتی sx
 * و رفع خطاهای سینتکسی. این نسخه امکان استایل‌دهی پویا بر اساس نیاز
 * مصرف‌کننده را فراهم می‌کند و ساختار JSX آن نیز استانداردسازی شده‌است.
 *
 * @template T نوع مقدار گزینه‌ها (string | number)
 * @param label برچسب نمایش‌داده‌شده کنار سلکت
 * @param options آرایه‌ گزینه‌های قابل انتخاب
 * @param value مقدار فعلی انتخاب‌شده
 * @param onChange تابع کنترل تغییر مقدار
 * @param sx استایل‌های قابل اعمال روی Box والد
 *
 * @returns JSX.Element
 */

function CustomSelect<T extends string | number>({
  label,
  options,
  value,
  onChange,
  sx,
}: CustomSelectProps<T>) {
  const handleChange = (event: SelectChangeEvent | any) => {
    const selectedValue = event.target.value;
    const coercedValue =
      typeof options[0] === "number" ? Number(selectedValue) : selectedValue;

    onChange(coercedValue);
  };

  return (
    <Box
      sx={{
        background:
          "linear-gradient(91.84deg, rgba(255, 255, 255, 0.123) -11.9%, rgba(255, 255, 255, 0.027) 109.66%)",
        borderRadius: "12px",
        padding: "10px 12px",
        display: "flex",
        alignItems: "center",
        gap: 1,
        height: "48px",
        ...sx,
      }}
    >
      <FormControl variant="standard" sx={{ minWidth: 50, height: "40px" }}>
        <Select
          value={value}
          onChange={handleChange}
          disableUnderline
          IconComponent={UnfoldMoreOutlinedIcon}
          fullWidth
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "#1C1C1C",
                color: "#fff",
                borderRadius: "8px",
                mt: 1,
                minWidth: "120px",
                height: "92px",
              },
            },
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
          }}
          sx={{
            background:
              "linear-gradient(125.22deg, #1C1C1C -8.79%, #303030 115.63%)",
            position: "relative",
            color: "#fff",
            borderRadius: "8px",
            textAlign: "center",
            fontSize: "14px",
            padding: "4px 8px",
            width: "100%",
            "& .MuiSelect-icon": {
              color: "#fff",
            },
          }}
        >
          {options?.map((opt, index) => (
            <MenuItem
              key={index}
              value={opt}
              sx={{
                color: "#fff",
                "& .MuiPopover-paper": {
                  minHeight: "130px !important",
                },
              }}
            >
              <TypoGraphyKit variant="body1">
                {typeof opt === "number" ? toPersianNumber(opt) : opt}
              </TypoGraphyKit>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <InputLabel
        sx={{
          color: "#fff",
          fontSize: "14px",
          whiteSpace: "nowrap",
          marginLeft: "10px",
          background: "transparent !important",
        }}
      >
        {label}
      </InputLabel>
    </Box>
  );
}

export default CustomSelect;
